import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeftOutlined,
  CrownOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  SaveOutlined,
  ThunderboltOutlined,
  UserOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Layout,
  message,
  Modal,
  Progress,
  Row,
  Space,
  Spin,
  Typography,
} from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import { AppLogo } from "@/Assets/Index";
import { UserContext } from "@/Context/UserContext";
import { useCredits } from "@/Context/CreditContext";
import DeleteAccountModal, { DATA_RETENTION_DAYS } from "@/Components/Common/DeleteAccountModal";
import BillingTab from "@/Pages/Billing/BillingTab";

const { Text, Title, Link } = Typography;
const { Content, Header } = Layout;
export default function Account() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fetch = useAuthenticatedFetch();
  const { userProfileData, updateProfileInfo } = useContext(UserContext);
  const { creditInfo, balance, refreshCredits } = useCredits();

  // Get initial tab from URL query param
  const initialTab = searchParams.get("tab") || "profile";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [creditBalance, setCreditBalance] = useState(null);

  const [passwordForm] = Form.useForm();
  const [nameForm] = Form.useForm();
  const [phoneForm] = Form.useForm();

  // Fetch user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      const authToken = localStorage.getItem("authorization");
      const userId = localStorage.getItem("userId");

      if (!authToken || !userId) {
        navigate("/app/login");
        return;
      }

      // If context already has data, use it
      if (userProfileData && userProfileData._id) {
        setUserData(userProfileData);
        setLoading(false);
        return;
      }

      // Fetch from API
      try {
        const response = await fetch.get(`/user/dashboard/${userId}`);
        if (response?.success && response?.data?.user) {
          setUserData(response.data.user);
          updateProfileInfo(response.data.user);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchCreditBalance = async () => {
      try {
        const response = await fetch.get("/credits/balance", false);
        if (response?.success) {
          setCreditBalance(response.data);
        }
      } catch (error) {
        console.error("Error fetching credit balance:", error);
      }
    };

    fetchUserData();
    fetchCreditBalance();
    refreshCredits();
  }, []);

  // Sync userData with context when it changes
  useEffect(() => {
    if (userProfileData && userProfileData._id) {
      setUserData(userProfileData);
      setLoading(false);
    }
  }, [userProfileData]);

  // Handle name update
  const [nameLoading, setNameLoading] = useState(false);

  const handleNameUpdate = async (values) => {
    setNameLoading(true);
    try {
      const response = await fetch.put("/user", {
        firstName: values.firstName,
        lastName: values.lastName,
      });
      if (response?.success || response?.data) {
        const updatedData = { ...userData, firstName: values.firstName, lastName: values.lastName };
        setUserData(updatedData);
        updateProfileInfo(updatedData);
        message.success("Name updated successfully!");
        setIsEditingName(false);
      } else {
        message.error(response?.message || "Failed to update name");
      }
    } catch (error) {
      console.error("Name update error:", error);
      message.error("Failed to update name");
    } finally {
      setNameLoading(false);
    }
  };

  // Handle phone update
  const [phoneLoading, setPhoneLoading] = useState(false);

  const handlePhoneUpdate = async (values) => {
    setPhoneLoading(true);
    try {
      const response = await fetch.put("/user", {
        mobile: values.mobile,
      });
      if (response?.success || response?.data) {
        const updatedData = { ...userData, mobile: values.mobile };
        setUserData(updatedData);
        updateProfileInfo(updatedData);
        message.success("Phone number updated successfully!");
        setIsEditingPhone(false);
      } else {
        message.error(response?.message || "Failed to update phone number");
      }
    } catch (error) {
      console.error("Phone update error:", error);
      message.error("Failed to update phone number");
    } finally {
      setPhoneLoading(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error("Passwords do not match!");
      return;
    }
    setPasswordLoading(true);
    try {
      const response = await fetch.post("/user/change-password", {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      if (response?.success) {
        message.success("Password changed successfully!");
        setIsPasswordModalOpen(false);
        passwordForm.resetFields();
      } else {
        message.error(response?.message || "Failed to change password");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to change password. Please try again.";
      message.error(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };


  const fullName = `${userData?.firstName || ""} ${userData?.lastName || ""}`.trim();

  // Menu items for sidebar
  const menuItems = [
    {
      key: "profile",
      label: "Your Profile",
      icon: <UserOutlined />,
    },
    {
      key: "billing",
      label: "Billing & Credits",
      icon: <WalletOutlined />,
    },
  ];

  // Render profile content directly (not as a separate component to avoid recreation)
  const profileContent = (
    <Row gutter={[24, 24]} style={{ alignItems: "stretch" }}>
      {/* Personal Information Card */}
      <Col xs={24} md={12} style={{ display: "flex" }}>
        <Card
          style={{
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            width: "100%",
          }}
        >
          <Title level={4} style={{ marginBottom: "24px", color: "#1f2937" }}>
            <UserOutlined style={{ marginRight: "8px", color: "#1990ff" }} />
            Personal Information
          </Title>

          {/* Full Name */}
          <div style={{ marginBottom: "20px" }}>
            <Text
              style={{ display: "block", marginBottom: "6px", color: "#6b7280", fontSize: "13px" }}
            >
              Full Name
            </Text>
            {!isEditingName ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Text strong style={{ fontSize: "16px", color: "#1f2937" }}>
                  {fullName || "Not set"}
                </Text>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => {
                    nameForm.setFieldsValue({
                      firstName: userData?.firstName,
                      lastName: userData?.lastName,
                    });
                    setIsEditingName(true);
                  }}
                  style={{ color: "#1990ff" }}
                />
              </div>
            ) : (
              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Form form={nameForm} onFinish={handleNameUpdate} layout="vertical">
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item
                        name="firstName"
                        label={<Text style={{ color: "#64748b", fontSize: "12px" }}>First Name</Text>}
                        style={{ marginBottom: "12px" }}
                        rules={[{ required: true, message: "Please enter first name" }]}
                      >
                        <Input
                          placeholder="Enter first name"
                          size="large"
                          style={{
                            borderRadius: "8px",
                            border: "1px solid #e2e8f0",
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="lastName"
                        label={<Text style={{ color: "#64748b", fontSize: "12px" }}>Last Name</Text>}
                        style={{ marginBottom: "12px" }}
                        rules={[{ required: true, message: "Please enter last name" }]}
                      >
                        <Input
                          placeholder="Enter last name"
                          size="large"
                          style={{
                            borderRadius: "8px",
                            border: "1px solid #e2e8f0",
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                    <Button
                      onClick={() => setIsEditingName(false)}
                      style={{
                        borderRadius: "8px",
                        height: "36px",
                        padding: "0 16px",
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={nameLoading}
                      icon={<SaveOutlined />}
                      style={{
                        background: "#1990ff",
                        border: "none",
                        borderRadius: "8px",
                        height: "36px",
                        padding: "0 16px",
                      }}
                    >
                      Save Changes
                    </Button>
                  </div>
                </Form>
              </div>
            )}
          </div>

          <Divider style={{ margin: "16px 0" }} />

          {/* Email */}
          <div style={{ marginBottom: "20px" }}>
            <Text
              style={{ display: "block", marginBottom: "6px", color: "#6b7280", fontSize: "13px" }}
            >
              Email Address
            </Text>
            <div style={{ display: "flex", alignItems: "center" }}>
              <MailOutlined style={{ marginRight: "8px", color: "#1990ff" }} />
              <Text style={{ fontSize: "15px", color: "#1f2937" }}>
                {userData?.email || "Not set"}
              </Text>
            </div>
          </div>

          <Divider style={{ margin: "16px 0" }} />

          {/* Phone */}
          <div style={{ marginBottom: "20px" }}>
            <Text
              style={{ display: "block", marginBottom: "6px", color: "#6b7280", fontSize: "13px" }}
            >
              Phone Number
            </Text>
            {!isEditingPhone ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <PhoneOutlined style={{ marginRight: "8px", color: "#1990ff" }} />
                  <Text style={{ fontSize: "15px", color: "#1f2937" }}>
                    {userData?.mobile || "Not set"}
                  </Text>
                </div>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => {
                    phoneForm.setFieldsValue({ mobile: userData?.mobile });
                    setIsEditingPhone(true);
                  }}
                  style={{ color: "#1990ff" }}
                />
              </div>
            ) : (
              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Form form={phoneForm} onFinish={handlePhoneUpdate} layout="vertical">
                  <Form.Item
                    name="mobile"
                    label={<Text style={{ color: "#64748b", fontSize: "12px" }}>Phone Number</Text>}
                    style={{ marginBottom: "12px" }}
                    rules={[
                      { required: true, message: "Please enter phone number" },
                      { pattern: /^[\+]?[\d\s\-\(\)]{10,20}$/, message: "Please enter a valid phone number" },
                    ]}
                  >
                    <Input
                      placeholder="Enter phone number"
                      size="large"
                      style={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                    />
                  </Form.Item>
                  <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                    <Button
                      onClick={() => setIsEditingPhone(false)}
                      style={{ borderRadius: "8px", height: "36px", padding: "0 16px" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={phoneLoading}
                      icon={<SaveOutlined />}
                      style={{
                        background: "#1990ff",
                        border: "none",
                        borderRadius: "8px",
                        height: "36px",
                        padding: "0 16px",
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </Form>
              </div>
            )}
          </div>

          <Divider style={{ margin: "16px 0" }} />

          {/* Password */}
          <div>
            <Text
              style={{ display: "block", marginBottom: "6px", color: "#6b7280", fontSize: "13px" }}
            >
              Password
            </Text>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <LockOutlined style={{ marginRight: "8px", color: "#1990ff" }} />
                <Text style={{ fontSize: "15px", color: "#1f2937" }}>••••••••••••</Text>
              </div>
              <Link
                style={{ color: "#1990ff", fontWeight: 500 }}
                onClick={() => setIsPasswordModalOpen(true)}
              >
                Change Password
              </Link>
            </div>
          </div>
        </Card>
      </Col>

      {/* Current Plan & Credits Card */}
      <Col xs={24} md={12} style={{ display: "flex" }}>
        <Card
          style={{
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            width: "100%",
          }}
        >
          <Title level={4} style={{ marginBottom: "10px", color: "#1f2937" }}>
            <ThunderboltOutlined style={{ marginRight: "8px", color: "#faad14" }} />
            Current Plan & Credits
          </Title>


          <Divider style={{ margin: "8px 0" }} />

          {/* Credits Balance */}
          <div style={{ marginBottom: "10px" }}>
            <Text
              style={{ display: "block", marginBottom: "6px", color: "#64748b", fontSize: "11px", fontWeight: 500 }}
            >
              Credits Balance
            </Text>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                background: (creditBalance?.balance ?? balance ?? 0) > 0
                  ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)"
                  : "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
                borderRadius: "8px",
                border: (creditBalance?.balance ?? balance ?? 0) > 0 ? "1.5px solid #86efac" : "1.5px solid #fca5a5",
                boxShadow: (creditBalance?.balance ?? balance ?? 0) > 0
                  ? "0 2px 8px rgba(34, 197, 94, 0.12)"
                  : "0 2px 8px rgba(239, 68, 68, 0.12)",
              }}
            >
              <div>
                <Text
                  style={{
                    fontSize: "22px",
                    fontWeight: 700,
                    color: (creditBalance?.balance ?? balance ?? 0) > 0 ? "#15803d" : "#dc2626",
                    lineHeight: 1,
                    letterSpacing: "-0.5px",
                  }}
                >
                  {creditBalance?.balance ?? balance ?? 0}
                </Text>
                <Text
                  style={{
                    display: "block",
                    fontSize: "10px",
                    color: (creditBalance?.balance ?? balance ?? 0) > 0 ? "#166534" : "#991b1b",
                    marginTop: "3px",
                    fontWeight: 500,
                  }}
                >
                  Credits Available
                </Text>
              </div>
              <ThunderboltOutlined
                style={{
                  fontSize: "22px",
                  color: (creditBalance?.balance ?? balance ?? 0) > 0 ? "#22c55e" : "#f87171",
                  opacity: 0.5,
                }}
              />
            </div>
          </div>

          {/* Credits Usage Stats */}
          <div style={{ marginBottom: "10px" }}>
            <Row gutter={8}>
              <Col span={12}>
                <div
                  style={{
                    padding: "8px 10px",
                    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                    borderRadius: "7px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
                  }}
                >
                  <Text style={{ display: "block", fontSize: "10px", color: "#64748b", marginBottom: "2px", fontWeight: 500 }}>
                    Total Used
                  </Text>
                  <Text strong style={{ fontSize: "16px", color: "#1e293b", letterSpacing: "-0.3px" }}>
                    {creditBalance?.totalUsed ?? creditInfo?.totalUsed ?? 0}
                  </Text>
                </div>
              </Col>
              <Col span={12}>
                <div
                  style={{
                    padding: "8px 10px",
                    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                    borderRadius: "7px",
                    border: "1px solid #93c5fd",
                    boxShadow: "0 1px 3px rgba(59, 130, 246, 0.08)",
                  }}
                >
                  <Text style={{ display: "block", fontSize: "10px", color: "#1e40af", marginBottom: "2px", fontWeight: 500 }}>
                    Free Credits
                  </Text>
                  <Text strong style={{ fontSize: "16px", color: "#1e3a8a", letterSpacing: "-0.3px" }}>
                    {creditBalance?.freeCreditsGiven ?? creditInfo?.freeCreditsGiven ?? 30}
                  </Text>
                </div>
              </Col>
            </Row>
          </div>

          <Divider style={{ margin: "16px 0" }} />

          {/* Add Credits Button */}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => setActiveTab("billing")}
            style={{
              width: "100%",
              height: "48px",
              background: "linear-gradient(135deg, #1890ff 0%, #0050b3 100%)",
              border: "none",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: "15px",
              boxShadow: "0 4px 12px rgba(24, 144, 255, 0.3)",
            }}
          >
            Add More Credits
          </Button>
        </Card>
      </Col>

      {/* Danger Zone */}
      <Col xs={24}>
        <Card
          style={{
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: "1px solid #ffccc7",
            width: "100%",
          }}
        >
          <Title level={4} style={{ marginBottom: "10px", color: "#cf1322" }}>
            <ExclamationCircleOutlined style={{ marginRight: "8px" }} />
            Delete Account
          </Title>
          <Text style={{ color: "#6b7280", fontSize: "14px" }}>
            Deleting your account removes access to your resumes, cover letters and credits right away.
            All of your data is then permanently removed within {DATA_RETENTION_DAYS} days. This action
            cannot be undone.
          </Text>
          <div style={{ marginTop: "16px" }}>
            <Button
              danger
              type="primary"
              icon={<DeleteOutlined />}
              onClick={() => setIsDeleteModalOpen(true)}
              style={{ borderRadius: "10px", fontWeight: 600 }}
            >
              Delete Account
            </Button>
          </div>
        </Card>
      </Col>
    </Row>
  );

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case "billing":
        return <BillingTab />;
      case "profile":
      default:
        return profileContent;
    }
  };

  // Show loading spinner while fetching data
  if (loading) {
    return (
      <Layout style={{ minHeight: "100vh", background: "#f5f7fa" }}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f7fa", margin: 0, padding: 0 }}>
      <style>{`
        body { margin: 0 !important; padding: 0 !important; }
        .ant-layout { margin: 0 !important; padding: 0 !important; }
      `}</style>

      {/* Header */}
      <Header
        style={{
          background: "#fff",
          padding: "0 24px",
          height: "64px",
          display: "flex",
          alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <img
          src={AppLogo}
          alt="Upresume.ai"
          style={{ height: "40px", cursor: "pointer" }}
          onClick={() => navigate("/app/dashboard")}
        />
      </Header>

      {/* Content with Sidebar */}
      <Layout style={{ background: "#f5f7fa" }}>
        {/* Sidebar Menu */}
        <Layout.Sider
          width={240}
          style={{
            background: "#fff",
            padding: "24px 0",
            minHeight: "calc(100vh - 64px)",
          }}
        >
          {/* Back Button */}
          <div
            onClick={() => navigate("/app/dashboard")}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "1px 14px",
              cursor: "pointer",
              marginBottom: "12px",
            }}
          >
            <ArrowLeftOutlined style={{ color: "#1990ff", marginRight: "10px", fontSize: "14px" }} />
            <Text style={{ color: "#1990ff", fontWeight: 500, fontSize: "15px" }}>back</Text>
          </div>

          {/* Menu Items */}
          {menuItems.map((item) => (
            <div
              key={item.key}
              onClick={() => setActiveTab(item.key)}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "10px 14px",
                cursor: "pointer",
                background: activeTab === item.key ? "#f3f4f6" : "transparent",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (activeTab !== item.key) {
                  e.currentTarget.style.background = "#f9fafb";
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== item.key) {
                  e.currentTarget.style.background = "transparent";
                }
              }}
            >
              <span style={{
                marginRight: "12px",
                fontSize: "16px",
                color: activeTab === item.key ? "#1990ff" : "#6b7280"
              }}>
                {item.icon}
              </span>
              <Text style={{
                fontSize: "15px",
                fontWeight: activeTab === item.key ? 500 : 400,
                color: activeTab === item.key ? "#1f2937" : "#4b5563"
              }}>
                {item.label}
              </Text>
            </div>
          ))}
        </Layout.Sider>

        {/* Main Content */}
        <Content style={{ marginTop:"20px",padding: "32px", background: "#f5f7fa" }}>
          {renderContent()}
        </Content>
      </Layout>

      {/* Change Password Modal */}
      <Modal
        title="Change Password"
        open={isPasswordModalOpen}
        onCancel={() => {
          setIsPasswordModalOpen(false);
          passwordForm.resetFields();
        }}
        footer={null}
        centered
      >
        <Form form={passwordForm} layout="vertical" onFinish={handlePasswordChange}>
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[{ required: true, message: "Please enter current password" }]}
          >
            <Input.Password
              size="large"
              placeholder="Enter current password"
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: "Please enter new password" },
              { min: 8, message: "Password must be at least 8 characters" },
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Enter new password"
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            rules={[
              { required: true, message: "Please confirm new password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Confirm new password"
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, marginTop: "24px" }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={() => { setIsPasswordModalOpen(false); passwordForm.resetFields(); }}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={passwordLoading}
                style={{ background: "#1990ff", border: "none" }}
              >
                Update Password
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <DeleteAccountModal open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} />
    </Layout>
  );
}
